/**
 * AutoNamePreview — after Auto Name (or Auto Name Folder) reads the selected
 * title-block region on every page, this dialog shows EXACTLY what was read
 * and the proposed sheet name per page. Every name is editable before
 * applying; pages with no readable text layer offer a per-page "Read with
 * AI" button (vision OCR). Nothing is written until Apply is clicked, and
 * existing names are preserved unless a replacement is present.
 */
import { useState } from "react";
import { Sparkles, X } from "lucide-react";

export default function AutoNamePreview({ rows, onApply, onOcr, onClose }) {
  const [names, setNames] = useState(() => Object.fromEntries(rows.map((r) => [`${r.fileId}:${r.page}`, r.proposed || ""])));
  const [include, setInclude] = useState(() => Object.fromEntries(rows.map((r) => [`${r.fileId}:${r.page}`, !!r.proposed])));
  const [busyKey, setBusyKey] = useState(null);

  const setName = (k, v) => setNames((n) => ({ ...n, [k]: v }));
  const applicable = rows.filter((r) => include[`${r.fileId}:${r.page}`] && (names[`${r.fileId}:${r.page}`] || "").trim());

  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/60 p-4" data-testid="autoname-preview">
      <div className="flex max-h-[85%] w-[560px] max-w-full flex-col rounded-xl border border-[#24324a] bg-[#0b172a] shadow-2xl">
        <div className="flex items-center gap-2 border-b border-[#24324a] px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white">Auto name — check before applying</p>
            <p className="text-[11px] text-white/45">Edit any name, uncheck to skip. Nothing is saved until you apply.</p>
          </div>
          <button onClick={onClose} aria-label="Close auto name preview" className="rounded p-1 text-white/50 hover:bg-white/10 hover:text-white" data-testid="autoname-preview-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
          {rows.map((r) => {
            const key = `${r.fileId}:${r.page}`;
            const noText = !r.hasText;
            return (
              <div key={key} className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-2.5 py-2" data-testid={`autoname-row-${key}`}>
                <input type="checkbox" checked={!!include[key]} onChange={() => setInclude((s) => ({ ...s, [key]: !s[key] }))}
                  aria-label={`Include page ${r.page}`} className="accent-[#f97316]" data-testid={`autoname-include-${key}`} />
                <div className="w-24 shrink-0 truncate text-[10px] text-white/50" title={r.drawingName ? `${r.drawingName} · page ${r.page}` : `Page ${r.page}`}>
                  {r.drawingName ? `${r.drawingName} · ` : ""}p.{r.page}
                </div>
                <input
                  value={names[key]}
                  onChange={(e) => setName(key, e.target.value)}
                  placeholder={noText ? "No text layer" : "—"}
                  className="h-7 min-w-0 flex-1 rounded border border-white/15 bg-white/[0.06] px-2 font-mono text-xs text-white placeholder:text-white/30 focus:border-[#f97316] focus:outline-none"
                  data-testid={`autoname-name-${key}`}
                />
                {r.source === "vision" && <span className="shrink-0 rounded bg-purple-500/20 px-1.5 py-0.5 text-[9px] font-bold text-purple-300" title="Read with AI vision">AI</span>}
                {noText && (
                  <button
                    disabled={busyKey === key}
                    onClick={async () => {
                      setBusyKey(key);
                      try {
                        const res = await onOcr(r.fileId, r.page);
                        if (res?.proposed) setName(key, res.proposed);
                      } finally { setBusyKey(null); }
                    }}
                    className="flex shrink-0 items-center gap-1 rounded bg-purple-600/80 px-2 py-1 text-[10px] font-semibold text-white hover:bg-purple-500 disabled:opacity-50"
                    title="This page has no readable text layer — read the region with AI vision (uses a little LLM credit)"
                    data-testid={`autoname-ocr-${key}`}>
                    <Sparkles className="h-3 w-3" />{busyKey === key ? "Reading…" : "Read with AI"}
                  </button>
                )}
                {r.text && !noText && (
                  <span className="shrink-0 cursor-help text-[10px] text-white/25" title={`Read from region: "${r.text}"`}>?</span>
                )}
              </div>
            );
          })}
          {!rows.length && <p className="px-3 py-6 text-center text-xs text-white/35">Nothing to name.</p>}
        </div>
        <div className="flex items-center justify-between border-t border-[#24324a] px-4 py-3">
          <p className="text-[11px] text-white/40" data-testid="autoname-preview-count">{applicable.length} of {rows.length} will be named</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-md px-3 py-1.5 text-xs font-semibold text-white/60 hover:bg-white/10" data-testid="autoname-preview-cancel">Cancel</button>
            <button
              disabled={!applicable.length}
              onClick={() => onApply(applicable.map((r) => ({ fileId: r.fileId, page: r.page, name: (names[`${r.fileId}:${r.page}`] || "").trim() })))}
              className="rounded-md bg-[#f97316] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#fb923c] disabled:opacity-40"
              data-testid="autoname-preview-apply">
              Apply names
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
